/**
 * First-touch auth surface tracking for signup attribution.
 * Persists the first qualifying auth surface in the session through OAuth redirects.
 */

const AUTH_SURFACE_KEY = 'treechat_first_auth_surface'
const AUTH_SURFACE_TIMESTAMP_KEY = 'treechat_first_auth_surface_timestamp'
const AUTH_SURFACE_COOKIE_KEY = 'signup_auth_surface'
const SIGNUP_METHOD_KEY = 'treechat_signup_method'
const SESSION_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes
const VALID_SIGNUP_METHODS = ['email', 'wallet', 'google', 'x']

/**
 * Qualifying surfaces for signup attribution.
 * These are the surfaces where users can initiate signup.
 */
const QUALIFYING_SURFACES = {
    LANDING_CTA: 'landing_cta', // Landing page CTA buttons
    MODAL: 'modal', // Auth modal overlay
    QUEST: 'quest', // Quest view auth prompt
    STREAM: 'stream', // Stream view auth prompt
    DIRECT: 'direct', // Direct navigation to /signup or /auth
}

function isQualifyingSurface(surface) {
    return Object.values(QUALIFYING_SURFACES).includes(surface)
}

/**
 * Set a cookie value.
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} maxAgeSeconds - Max age in seconds
 */
function setCookie(name, value, maxAgeSeconds = 1800) {
    try {
        const secure = window.location.protocol === 'https:' ? '; Secure' : ''
        document.cookie = `${name}=${encodeURIComponent(
            value,
        )}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${secure}`
    } catch (e) {
        console.error('Failed to set cookie:', e)
    }
}

/**
 * Get a cookie value.
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null
 */
function getCookie(name) {
    try {
        const value = `; ${document.cookie}`
        const parts = value.split(`; ${name}=`)
        if (parts.length === 2) {
            return decodeURIComponent(parts.pop().split(';').shift())
        }
    } catch (e) {
        console.error('Failed to read cookie:', e)
    }
    return null
}

/**
 * Set the first-touch auth surface if not already set.
 * Persists to both sessionStorage AND cookie so server can read it.
 * @param {string} surface - One of QUALIFYING_SURFACES values
 */
export function setFirstTouchAuthSurface(surface) {
    if (!isQualifyingSurface(surface)) {
        console.warn(`Invalid auth surface: ${surface}`)
        return
    }

    const existing = getFirstTouchAuthSurface()
    if (existing) {
        // Already set in this session, don't overwrite
        return
    }

    try {
        sessionStorage.setItem(AUTH_SURFACE_KEY, surface)
        sessionStorage.setItem(AUTH_SURFACE_TIMESTAMP_KEY, Date.now().toString())
    } catch (e) {
        console.error('Failed to store auth surface:', e)
    }

    // Keep the server-readable fallback independent from sessionStorage. Some browsers can
    // reject storage access while still allowing a first-party cookie.
    setCookie(AUTH_SURFACE_COOKIE_KEY, surface, 1800)
}

/**
 * Get the first-touch auth surface from this session.
 * Checks sessionStorage first, then cookie (for OAuth returns).
 * Returns null if none set or session expired.
 * @returns {string|null} The auth surface or null
 */
export function getFirstTouchAuthSurface() {
    let surface = null
    let timestamp = null

    try {
        surface = sessionStorage.getItem(AUTH_SURFACE_KEY)
        timestamp = sessionStorage.getItem(AUTH_SURFACE_TIMESTAMP_KEY)
    } catch (e) {
        console.error('Failed to read auth surface:', e)
    }

    const timestampMs = Number(timestamp)
    const hasCurrentTimestamp =
        Number.isFinite(timestampMs) && Date.now() - timestampMs <= SESSION_TIMEOUT_MS
    if (isQualifyingSurface(surface) && hasCurrentTimestamp) return surface

    try {
        sessionStorage.removeItem(AUTH_SURFACE_KEY)
        sessionStorage.removeItem(AUTH_SURFACE_TIMESTAMP_KEY)
    } catch (_) {}

    const cookieSurface = getCookie(AUTH_SURFACE_COOKIE_KEY)
    return isQualifyingSurface(cookieSurface) ? cookieSurface : null
}

/**
 * Get auth surface for tracking, defaulting to 'direct' if not set.
 * @returns {string} The auth surface for analytics
 */
export function getAuthSurfaceForTracking() {
    return getFirstTouchAuthSurface() || QUALIFYING_SURFACES.DIRECT
}

/**
 * Set the signup method for tracking.
 * @param {string} method - One of: email, wallet, google, x
 */
export function setSignupMethod(method) {
    if (!VALID_SIGNUP_METHODS.includes(method)) {
        console.warn(`Invalid signup method: ${method}`)
        return
    }

    try {
        sessionStorage.setItem(SIGNUP_METHOD_KEY, method)
    } catch (e) {
        console.error('Failed to store signup method:', e)
    }
}

/**
 * Get the signup method from session storage.
 * @returns {string} The signup method, defaults to 'email' if not set
 */
export function getSignupMethod() {
    try {
        const method = sessionStorage.getItem(SIGNUP_METHOD_KEY)
        return VALID_SIGNUP_METHODS.includes(method) ? method : 'email'
    } catch (e) {
        console.error('Failed to read signup method:', e)
        return 'email'
    }
}

/**
 * Delete a cookie.
 * @param {string} name - Cookie name
 */
function deleteCookie(name) {
    try {
        document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`
    } catch (e) {
        console.error('Failed to delete cookie:', e)
    }
}

/**
 * Clear the stored auth surface and signup method (e.g., after successful signup prompt interaction).
 */
export function clearAuthSurface() {
    try {
        sessionStorage.removeItem(AUTH_SURFACE_KEY)
        sessionStorage.removeItem(AUTH_SURFACE_TIMESTAMP_KEY)
        sessionStorage.removeItem(SIGNUP_METHOD_KEY)
    } catch (e) {
        console.error('Failed to clear auth surface:', e)
    }

    deleteCookie(AUTH_SURFACE_COOKIE_KEY)
}

export { QUALIFYING_SURFACES }
